import type { LevelFinishResponse, LevelStartResponse } from "@monekineko/shared";
import {
  BackIcon,
  Button,
  GameIconButton,
  HintCounter,
  LivesCounter,
  NeonButton,
  Spinner,
  StarRating,
} from "@monekineko/ui-kit";
import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, api, createEventQueue } from "../api.js";
import { getEngine, sfx, type GameEngine } from "@monekineko/game-ui";
import { useSession } from "../state/session.js";
import { haptic, notify } from "../telegram.js";
import styles from "./screens.module.css";

// Экран уровня: заводит движок механики, даёт ему отправлять решение и
// показывает итог. Сам движок про сеть ничего не знает.

type Stage = "loading" | "playing" | "result" | "error";

export function LevelScreen({ levelId, onExit }: { levelId: string; onExit: () => void }) {
  const { refreshProfile, reload, profile } = useSession();
  const hostRef = useRef<HTMLDivElement | null>(null);
  const engineRef = useRef<GameEngine | null>(null);
  const instanceRef = useRef<LevelStartResponse | null>(null);

  const [stage, setStage] = useState<Stage>("loading");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<LevelFinishResponse | null>(null);
  const [level, setLevel] = useState<LevelStartResponse | null>(null);
  const [hints, setHints] = useState(0);
  const [hintBusy, setHintBusy] = useState(false);
  const [canHint, setCanHint] = useState(false);
  // Ролик идёт — показываем «Пропустить». Как только он доиграл или его
  // пропустили, кнопка обязана исчезнуть, иначе она висит поверх игры.
  const [introPlaying, setIntroPlaying] = useState(false);
  const stageRef = useRef<Stage>("loading");
  stageRef.current = stage;

  const start = useCallback(async () => {
    setStage("loading");
    setResult(null);
    setError(null);

    try {
      const data = await api.startLevel(levelId);
      instanceRef.current = data;
      setLevel(data);
      setHints(data.hintsAvailable);
      setIntroPlaying(data.playIntro);
      setStage("playing");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Уровень не открылся");
      setStage("error");
    }
  }, [levelId]);

  useEffect(() => {
    void start();
  }, [start]);

  // Монтирование механики: движок живёт вне React и сам рисует свою сцену
  useEffect(() => {
    if (stage !== "playing" || !level || !hostRef.current) return;

    const factory = getEngine(level.gameType);
    if (!factory) {
      setError(`Тип игры «${level.gameType}» пока не поддерживается приложением`);
      setStage("error");
      return;
    }

    sfx.unlock();
    const queue = createEventQueue(level.instanceId);
    const host = hostRef.current;

    const engine = factory(host, {
      payload: level.payload,
      // настройки уровня остаются на сервере — механике хватает payload
      settings: {},
      playIntro: level.playIntro,
      event: (type, payload) => queue.push(type, payload),
      introDone: () => {
        setIntroPlaying(false);
        setCanHint(true);
        if (level.playIntro) void api.introSeen(level.levelId).catch(() => undefined);
      },
      check: async (solution) => {
        await queue.flush();
        return api.check(level.instanceId, solution);
      },
      submit: async (solution) => {
        await queue.flush();
        const verdict = await api.finish(level.instanceId, solution);
        if (verdict.solved) notify("success");
        else notify("error");
        return verdict;
      },
      finished: (verdict) => {
        setResult(verdict);
        setStage("result");
        void refreshProfile();
      },
    });

    engineRef.current = engine;
    setCanHint(Boolean(engine.canHint?.()) && !level.playIntro);

    return () => {
      engine.destroy();
      engineRef.current = null;
      void queue.flush();
    };
  }, [stage, level, refreshProfile]);

  // Уход с уровня: заход закрывается, чтобы не висел активным
  useEffect(() => {
    return () => {
      const instance = instanceRef.current;
      if (instance && stageRef.current !== "result") {
        void api.abandon(instance.instanceId).catch(() => undefined);
      }
    };
  }, []);

  const takeHint = async (): Promise<void> => {
    const instance = instanceRef.current;
    if (!instance || hintBusy) return;
    setHintBusy(true);
    try {
      const data = await api.hint(instance.instanceId);
      engineRef.current?.applyHint?.(data.hint);
      setHints(data.hintsLeft);
      haptic("light");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Подсказка не пришла");
    } finally {
      setHintBusy(false);
    }
  };

  const leave = (): void => {
    const instance = instanceRef.current;
    if (instance && stage !== "result") void api.abandon(instance.instanceId).catch(() => undefined);
    void reload();
    onExit();
  };

  if (stage === "loading") {
    return (
      <div className={styles.center}>
        <Spinner size={32} />
        <p>Готовлю уровень…</p>
      </div>
    );
  }

  if (stage === "error") {
    return (
      <div className={styles.center}>
        <p className={styles.errorText}>{error}</p>
        <Button onClick={leave}>К списку уровней</Button>
      </div>
    );
  }

  return (
    <div className={styles.levelRoot}>
      <div className={styles.levelBar}>
        <GameIconButton label="Выйти из уровня" onClick={leave}>
          <BackIcon />
        </GameIconButton>

        <span className={styles.levelTitle}>{level?.title}</span>

        <span className={styles.levelBarRight}>
          {profile ? <LivesCounter lives={profile.lives} max={profile.livesMax} /> : null}
          {/* счётчик подсказок он же кнопка: место на телефоне дорогое */}
          <HintCounter hints={hints} busy={hintBusy} disabled={!canHint} onUse={() => void takeHint()} />
        </span>
      </div>

      <div className="game-frame" ref={hostRef} />

      {stage === "playing" && introPlaying ? (
        <button
          type="button"
          className={styles.skipIntro}
          onClick={() => {
            engineRef.current?.skipIntro?.();
            setIntroPlaying(false);
          }}
        >
          Пропустить ролик ›
        </button>
      ) : null}

      {stage === "result" && result ? (
        <ResultOverlay result={result} onRetry={() => void start()} onExit={leave} />
      ) : null}
    </div>
  );
}

const FAIL_REASON: Record<string, string> = {
  crashed: "Робот наехал на разрушенный сектор",
  sectors_left: "На диске остались сбойные сектора",
  stack_overflow: "Программа не влезла в стек",
  empty_program: "Программа пуста",
  command_not_allowed: "В программе команда, которой нет на пульте",
  wrong_value: "Число на замке не совпало",
  lamps_dark: "Не все лампочки загорелись",
  socket_empty: "Не все гнёзда заняты",
  chip_set_mismatch: "Набор микросхем разложен не полностью",
  too_fast: "Слишком быстро — похоже на подсказку извне",
};

function ResultOverlay({
  result,
  onRetry,
  onExit,
}: {
  result: LevelFinishResponse;
  onRetry: () => void;
  onExit: () => void;
}) {
  return (
    <div className={styles.overlay}>
      <div className={styles.overlayCard}>
        {result.solved ? (
          <>
            <StarRating value={result.stars} size={32} />
            <h2>Уровень пройден</h2>
            <p className={styles.overlayText}>
              Время: {Math.round(result.durationMs / 1000)} с · очков: +{result.points}
            </p>
            {result.outroText ? <p className={styles.overlayNote}>{result.outroText}</p> : null}
            {result.newAchievements.length ? (
              <p className={styles.overlayNote}>
                Новая ачивка: {result.newAchievements.map((item) => item.title).join(", ")}
              </p>
            ) : null}
            <NeonButton onClick={onExit}>Дальше</NeonButton>
          </>
        ) : (
          <>
            <h2>Не вышло</h2>
            <p className={styles.overlayText}>
              {FAIL_REASON[result.reason ?? ""] ?? "Уровень не засчитан"}
            </p>
            <p className={styles.overlayNote}>Жизней осталось: {result.lives}</p>
            {result.lives > 0 ? (
              <NeonButton onClick={onRetry}>Ещё раз</NeonButton>
            ) : (
              <p className={styles.overlayNote}>Жизни восстановятся сами — загляни позже</p>
            )}
            <Button variant="ghost" onClick={onExit}>
              К списку уровней
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
